import React, { useState } from 'react';
import {
  MessageCircle, Send, Volume2, VolumeX, ChevronLeft, ChevronRight, Clock,
  FileText, ExternalLink, AlertCircle, CheckCircle, Info, Sparkles
} from 'lucide-react';
import { answerQuery } from '../lib/askbotKnowledge';

// ==================== QUICK ANSWER BOT PAGE ====================
// Answers are produced by the fuzzy retrieval layer in lib/askbotKnowledge.js,
// which indexes both topic routing (jobs, special education, child protection,
// careers, scholarships, exams…) and the site's current data (scholarships,
// grants, loans, tertiary institutions).
export const QuickAnswerBotPage = ({ lang = 'en', setPage, goBack }) => {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [speaking, setSpeaking] = useState(false);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9;
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const runQuery = (q) => {
    const text = (q ?? '').trim();
    if (!text) return;
    const result = answerQuery(text);
    setAnswer(result);
    setHistory((prev) => [{
      query: text,
      result,
      time: new Date().toLocaleTimeString('en-JM', { hour: '2-digit', minute: '2-digit' })
    }, ...prev].slice(0, 10));
  };

  const handleSearch = () => runQuery(query);
  const handleKeyPress = (e) => { if (e.key === 'Enter') runQuery(query); };
  const handleSuggestion = (s) => { setQuery(s); runQuery(s); };

  // Suggested questions — showcase the broadened, natural-language coverage
  const suggestions = [
    'I need help paying for school',
    'How do I become a nurse?',
    'Teaching jobs',
    'My child has a learning disability',
    'How do I report child abuse?',
    'I dropped out — can I still study?',
    'Scholarships for STEM',
    'What is CSEC?'
  ];

  const headerTone =
    answer?.type === 'warning' ? 'bg-amber-100' :
    answer?.type === 'procedure' ? 'bg-blue-100' : 'bg-brand-100';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <ChevronLeft className="w-5 h-5" />Back
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black">Education Helper</h2>
            <p className="text-brand-100">Ask me anything — schools, exams, scholarships, careers, jobs and more</p>
          </div>
        </div>
      </div>

      {/* Search Input */}
      <div className="bg-white rounded-2xl shadow-lg p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Try “money for university”, “special needs”, “report abuse”…"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 text-lg"
          />
          <button
            onClick={handleSearch}
            className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-bold transition-colors flex items-center gap-2"
          >
            <Send className="w-5 h-5" />
            Ask
          </button>
        </div>

        {/* Suggestions */}
        {!answer && (
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestion(s)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-brand-100 text-gray-700 hover:text-brand-700 rounded-full text-sm transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Answer Display */}
      {answer && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Answer Header */}
          <div className={`px-6 py-4 ${headerTone}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {answer.type === 'warning' ? (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                ) : answer.type === 'procedure' ? (
                  <FileText className="w-6 h-6 text-blue-600" />
                ) : (
                  <Info className="w-6 h-6 text-brand-600" />
                )}
                <h3 className="text-xl font-bold text-gray-800">{answer.title}</h3>
              </div>
              <button
                onClick={() => speaking ? stopSpeaking() : speak(answer.answer)}
                className="p-2 rounded-lg hover:bg-white/50 transition-colors"
                title={speaking ? 'Stop' : 'Read aloud'}
              >
                {speaking ? (
                  <VolumeX className="w-5 h-5 text-gray-600" />
                ) : (
                  <Volume2 className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Answer Body */}
          <div className="p-6 space-y-4">
            <p className="text-gray-700 text-lg leading-relaxed">{answer.answer}</p>

            {/* Steps (for procedures) */}
            {answer.steps && (
              <div className="space-y-2">
                <h4 className="font-bold text-gray-800">Steps:</h4>
                <ol className="space-y-2">
                  {answer.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-brand-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Tips */}
            {answer.tips && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-brand-600" />
                  Key Points
                </h4>
                <ul className="space-y-2">
                  {answer.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <span className="text-brand-600">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Matched current data (scholarships, opportunities, institutions) */}
            {answer.results && answer.results.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-bold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-brand-600" />
                  Matches from our directory
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {answer.results.map((r, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
                      {r.category && (
                        <span className="self-start text-[11px] uppercase tracking-wide font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full mb-2">
                          {r.category}
                        </span>
                      )}
                      <h5 className="font-bold text-gray-800 leading-snug">{r.title}</h5>
                      {r.subtitle && <p className="text-sm text-gray-500 mt-0.5">{r.subtitle}</p>}
                      {r.note && <p className="text-sm text-gray-600 mt-1">{r.note}</p>}
                      <div className="mt-3 pt-2">
                        {r.url ? (
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 text-sm font-medium"
                          >
                            Learn more / Apply <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : r.page ? (
                          <button
                            onClick={() => setPage(r.page)}
                            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 text-sm font-medium"
                          >
                            View details <ChevronRight className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related directions (fallback / discovery) */}
            {answer.related && answer.related.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {answer.related.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(r.page)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-full text-sm font-medium transition-colors"
                  >
                    {r.text} <ChevronRight className="w-4 h-4" />
                  </button>
                ))}
              </div>
            )}

            {/* Primary link */}
            {answer.link && (
              <button
                onClick={() => setPage(answer.link.page)}
                className="flex items-center gap-2 text-brand-600 hover:text-brand-700 font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                {answer.link.text}
              </button>
            )}

            {/* Disclaimer */}
            {answer.disclaimer && (
              <p className="text-xs text-gray-400 flex items-start gap-1.5 pt-1">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                {answer.disclaimer}
              </p>
            )}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            Recent Questions
          </h3>
          <div className="space-y-2">
            {history.slice(0, 5).map((h, i) => (
              <button
                key={i}
                onClick={() => { setQuery(h.query); setAnswer(h.result); }}
                className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <p className="font-medium text-gray-800">{h.query}</p>
                <p className="text-sm text-gray-500">{h.time}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickAnswerBotPage;
